import { ClipboardModule } from '@angular/cdk/clipboard';
import { CdkDrag, CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { CommonModule, DatePipe } from '@angular/common';
import {
  HttpClientModule,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon'; // For the material icons
import { MatInputModule } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { AlertBannerComponent } from 'alert-banner-lib';
import { AuthInterceptor } from 'auth-service-lib';
import { BullhornRetryInterceptor } from 'datasource-service-lib';
import { DocumentTypePickerComponent } from 'document-type-picker-lib';
import { EntityPickerComponent } from 'entity-picker-lib';
import { Namespace } from 'enums-lib';
import { ENVIRONMENT, RUNTIME_ENVIRONMENT } from 'environments-lib';
import { HamburgerComponent } from 'hamburger-lib';
import { CompactNumberValuesPipe } from 'pipes-lib';
import { RippleDirective } from 'ripple-lib';
import { IdentityStorage } from 'storage-service-lib';
import { UserRestrictionsFilterComponent, UserRestrictionsPanelComponent } from 'user-restrictions-filter-lib';
import { environment, runtimeEnvironment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AiStarsGeneratingComponent } from './components/ai-stars-generating/ai-stars-generating.component';
import { AppManagerHeaderComponent } from './components/app-manager-header/app-manager-header.component';
import { AssistManagerAdminComponent } from './components/assist-manager-landing/assist-manager-admin/assist-manager-admin.component';
import { OptionsAutocompleteComponent } from './components/assist-manager-landing/assist-manager-admin/options-autocomplete/options-autocomplete.component';
import { AssistManagerLandingComponent } from './components/assist-manager-landing/assist-manager-landing.component';
import { PromptProfileComponent } from './components/assist-manager-landing/prompt-profile/prompt-profile.component';
import { AutoCompleteComponent } from './components/auto-complete/auto-complete.component';
import { AiResultComponent } from './components/bh-card/ai-result/ai-result.component';
import { BhCardComponent } from './components/bh-card/bh-card.component';
import { ConfigurePromptComponent } from './components/bh-card/configure-prompt/configure-prompt.component';
import { FilterPanelComponent } from './components/bh-card/configure-prompt/filter-panel/filter-panel.component';
import { PromptOptionSelectComponent } from './components/bh-card/configure-prompt/prompt-option-select/prompt-option-select.component';
import { CalendarSettingsDialogComponent } from './components/calendar-settings-dialog/calendar-settings-dialog.component';
import { ChipsComponent } from './components/chips/chips.component';
import { DialogConfirmComponent } from './components/dialog-confirm/dialog-confirm.component';
import { AssociatedEntitySelectorComponent } from './components/entity-selector/associated-entity-selector.component';
import { MultiEntitySelectComponent } from './components/entity-selector/multi-entity/multi-entity-select.component';
import { EntityTypePickerComponent } from './components/entity-type-picker/entity-type-picker.component';
import { HamburgerWrapperComponent } from './components/hamburger-wrapper/hamburger-wrapper.component';
import { LinkedEntitiesComponent } from './components/linked-entities/linked-entities.component';
import { LlmSelectComponent } from './components/llm-select/llm-select.component';
import { LoadingParticlesComponent } from './components/loading-particles/loading-particles.component';
import { ChatTypingComponent } from './components/loading-spinners/chat-typing/chat-typing.component';
import { LoadingSpinnerComponent } from './components/loading-spinners/loading-spinner/loading-spinner.component';
import { NotetakerCardComponent } from './components/notetaker-card/notetaker-card.component';
import { NotetakerResultComponent } from './components/notetaker-card/notetaker-result/notetaker-result.component';
import { UploadDialogComponent } from './components/notetaker-card/upload-dialog/upload-dialog.component';
import { NotetakerManagerComponent } from './components/notetaker-manager/notetaker-manager.component';
import { QuestionManagerComponent } from './components/notetaker-manager/question-manager/question-manager.component';
import { QuestionModalComponent } from './components/notetaker-manager/question-manager/question-modal/question-modal.component';
import { OptionSelectComponent } from './components/option-select/option-select.component';
import { PromptCarouselComponent } from './components/prompt-carousel/prompt-carousel.component';
import { PromptParagraphComponent } from './components/prompt-paragraph/prompt-paragraph.component';
import { SkeletonComponent } from './components/skeleton/skeleton.component';
import { NumberInputLimitDirective } from './directives/number-input-limits.directive';
import { WholeNumberOnlyDirective } from './directives/whole-number-only.directive';
import { ApiGatewayInterceptor } from './interceptors/api-gateway.interceptor';
import { HighlightPipe } from './pipes/highlight.pipe';
import { AiPromptService } from './services/ai-prompt.service';
import { AppBridgeService } from './services/app-bridge.service';

export function seedBullhornIdentityIfNeeded(identityStorage: IdentityStorage): void {
  if (new URLSearchParams(window.location.search).has('CorporationID') && !identityStorage.get()) {
    identityStorage.put({ namespace: Namespace.bullhorn, tenant: '', token: '' });
  }
}

@NgModule({
  declarations: [
    AppComponent,
    PromptCarouselComponent,
    AiStarsGeneratingComponent,
    AssociatedEntitySelectorComponent,
    ChatTypingComponent,
    PromptParagraphComponent,
    SkeletonComponent,
    AiStarsGeneratingComponent,
    OptionSelectComponent,
    BhCardComponent /* TODO: Rename to AiAssist */,
    NotetakerCardComponent,
    NotetakerResultComponent,
    NotetakerManagerComponent,
    AppManagerHeaderComponent,
    UploadDialogComponent,
    AiResultComponent,
    QuestionManagerComponent,
    QuestionModalComponent,
    ConfigurePromptComponent,
    PromptOptionSelectComponent,
    MultiEntitySelectComponent,
    AssistManagerLandingComponent,
    AssistManagerAdminComponent,
    OptionsAutocompleteComponent,
    PromptProfileComponent,
    DialogConfirmComponent,
    ChipsComponent,
    LoadingParticlesComponent,
    EntityTypePickerComponent,
    NumberInputLimitDirective,
    WholeNumberOnlyDirective,
    EntityTypePickerComponent,
    HighlightPipe,
    AutoCompleteComponent,
    FilterPanelComponent,
    LlmSelectComponent,
    HamburgerWrapperComponent,
    CalendarSettingsDialogComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    DocumentTypePickerComponent,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatIconModule,
    CommonModule,
    BrowserModule,
    HttpClientModule,
    ClipboardModule,
    MatSnackBarModule,
    BrowserAnimationsModule,
    MatTooltipModule,
    MatSelectModule,
    FormsModule,
    EditorComponent,
    MatDialogModule,
    MatChipsModule,
    MatExpansionModule,
    MatInputModule,
    CdkDropList,
    CdkDrag,
    ReactiveFormsModule,
    MatCheckboxModule,
    AppRoutingModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatPaginatorModule,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    CdkDropListGroup,
    MatSlideToggle,
    MatDatepicker,
    MatNativeDateModule,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatMenu,
    MatMenuTrigger,
    MatMenuItem,
    MatIcon,
    AlertBannerComponent,
    HamburgerComponent,
    EntityPickerComponent,
    LinkedEntitiesComponent,
    LoadingSpinnerComponent,
    RippleDirective,
    CompactNumberValuesPipe,
    UserRestrictionsFilterComponent,
    UserRestrictionsPanelComponent,
  ],
  providers: [
    AppBridgeService,
    AiPromptService,
    { provide: MAT_DIALOG_DATA, useValue: {} },
    provideAppInitializer(() => seedBullhornIdentityIfNeeded(inject(IdentityStorage))),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiGatewayInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: BullhornRetryInterceptor, multi: true },
    {
      provide: ENVIRONMENT,
      useValue: {
        ...environment,
        kpAdditionalAuthHosts: [environment.ntIntegrationApiUrl, environment.nt8x8ApiUrl]
          .filter((h): h is string => !!h),
      },
    },
    { provide: RUNTIME_ENVIRONMENT, useValue: runtimeEnvironment },
    DatePipe,
    provideHttpClient(withInterceptorsFromDi())
  ],
})
export class AppModule {}
